import BugsnagPerformance from "@bugsnag/browser-performance";
import Bugsnag from "@bugsnag/js";
import BugsnagPluginReact from "@bugsnag/plugin-react";
import { isRedirectError } from "next/dist/client/components/redirect-error";
import React from "react";
import { getApiUrl } from "@/config";
import { isKnownNextRepeatedSlashError } from "./bugsnag-error-filter";
import {
  isChunkLoadErrorLike,
  isStaleDeploymentError,
  reportStaleDeployment,
} from "./stale-deployment";

// このファイルはサーバー、ブラウザ共通で実行されます。
// Bugsnagの初期化など、副作用を伴う処理が含まれています。

const apiKey = process.env.NEXT_PUBLIC_BUGSNAG_API_KEY;
const releaseStage = process.env.NEXT_PUBLIC_ENV || "local";
const appVersion = process.env.NEXT_PUBLIC_APP_VERSION || "unknown";
const isBrowser = typeof window !== "undefined";
const redirectErrorMarker = "NEXT_REDIRECT";

let bugsnagStarted = false;

function isNextRedirectError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  if (isRedirectError(error as Error)) return true;
  const digest = (error as { digest?: unknown }).digest;
  if (typeof digest === "string" && digest.startsWith(redirectErrorMarker)) {
    return true;
  }
  const message = (error as { message?: unknown }).message;
  if (typeof message === "string" && message.includes(redirectErrorMarker)) {
    return true;
  }
  return false;
}

/**
 * jwt-decode が不正トークンに対して throw する InvalidTokenError かどうかを判定します。
 * Cookie 改ざんや切り詰め等で発生し、アプリ側で既に安全にハンドリングされているため通知不要です。
 */
function isJwtInvalidTokenError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const name = (error as { name?: unknown }).name;
  return name === "InvalidTokenError";
}

function initializeBugsnagCore(): void {
  if (bugsnagStarted) return;
  if (releaseStage === "local" || releaseStage === "development") {
    console.info("Bugsnag is disabled in local or development mode.");
    return;
  }
  if (!apiKey) {
    if (isBrowser) {
      console.warn(
        "Bugsnag API key is not configured. Bugsnag core will not be initialized for error reporting.",
      );
    }
    return;
  }

  const plugins = [];
  if (isBrowser) {
    plugins.push(new BugsnagPluginReact(React));
  }

  try {
    Bugsnag.start({
      apiKey: apiKey,
      plugins: plugins,
      releaseStage: releaseStage,
      appVersion: appVersion,
      appType: isBrowser ? "browser" : "server",
      logger: null, // src/lib/logger.tsで使う際に、console.logとbugsnag経由のlogが両方出力されるのを防ぐ
      onError: (event) => {
        // App Router の redirect は例外で制御されるため、通知対象から除外する
        const originalError = event.originalError;
        if (isNextRedirectError(originalError)) {
          return false;
        }
        // jwt-decode の InvalidTokenError は防御的にハンドリング済みのため除外する
        if (isJwtInvalidTokenError(originalError)) {
          return false;
        }
        const errors = (
          event as {
            errors?: Array<{ errorClass?: string; errorMessage?: string }>;
          }
        ).errors;
        const [firstError] = errors ?? [];
        if (firstError?.errorClass?.includes(redirectErrorMarker)) {
          return false;
        }
        if (firstError?.errorMessage?.includes(redirectErrorMarker)) {
          return false;
        }
        if (firstError?.errorClass === "InvalidTokenError") {
          return false;
        }
        // deployment skew による chunk 読み込み失敗はアプリ側の不具合ではないため、
        // 通知せず再読み込み導線に振り替える。global handler 経由の未捕捉例外も
        // logger.error() を通らずここに来るので、この分岐で拾う必要がある。
        if (
          isStaleDeploymentError(originalError) ||
          isChunkLoadErrorLike({
            errorClass: firstError?.errorClass,
            errorMessage: firstError?.errorMessage,
          })
        ) {
          if (isBrowser) {
            reportStaleDeployment();
          }
          return false;
        }
        // 不正な先頭重複スラッシュで発生する Next.js の既知例外は通知対象から除外する
        if (
          isBrowser &&
          isKnownNextRepeatedSlashError({
            pathname: window.location.pathname,
            originalError,
            errorClass: firstError?.errorClass,
            errorMessage: firstError?.errorMessage,
          })
        ) {
          return false;
        }
        return true;
      },
    });
    bugsnagStarted = true;
    if (isBrowser) {
      BugsnagPerformance.start({
        apiKey: apiKey,
        bugsnag: Bugsnag,
        appVersion: appVersion,
        releaseStage: releaseStage,
        // The SDK owns the browser network span; only these app endpoints receive its context.
        networkRequestCallback: (request) => {
          if (!request.url) return request;
          try {
            const requestUrl = new URL(request.url, window.location.href);
            const browserUrl = new URL(window.location.href);
            const graphqlApiUrl = new URL(getApiUrl());
            return {
              ...request,
              propagateTraceContext:
                (requestUrl.origin === browserUrl.origin &&
                  requestUrl.pathname === "/api/chat") ||
                (requestUrl.origin === graphqlApiUrl.origin &&
                  requestUrl.pathname === graphqlApiUrl.pathname),
            };
          } catch {
            return request;
          }
        },
      });
      console.log(
        `Bugsnag core and performance initialized for release stage: ${releaseStage}, appVersion: ${appVersion}. React plugin loaded.`,
      );
    } else {
      console.log(
        `Bugsnag core initialized for release stage: ${releaseStage}, appVersion: ${appVersion} (server-side).`,
      );
    }
  } catch (error) {
    if (isBrowser) {
      console.error("Failed to initialize Bugsnag core:", error);
    } else {
      console.error("Failed to initialize Bugsnag core (server-side):", error);
    }
  }
}

initializeBugsnagCore();

export { Bugsnag, bugsnagStarted };
